import type { ExportFormat } from "../constants";

export type FileNameErrorKey = "required" | "invalid-chars" | "too-long";

const INVALID_CHARS = /[/\\:*?"<>|]/;
const MAX_LENGTH = 255;

/**
 * Convert a string to a URL-friendly slug.
 * Lowercases, replaces spaces and non-alphanumeric runs with hyphens,
 * and trims leading/trailing hyphens.
 */
const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Format a Date as YYYY-MM-DD using native methods.
 */
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Generate the default file name based on format and optional saved view name.
 * Date portion uses YYYY-MM-DD format.
 */
export const generateDefaultFileName = (
  format: ExportFormat,
  savedViewName?: string
): string => {
  const today = formatDate(new Date());
  const ext = format === "bai2" ? "bai2" : "csv";
  const baseName = savedViewName
    ? `${slugify(savedViewName) || "export"}-result_${today}`
    : `result_${today}`;
  return `${baseName}.${ext}`;
};

/**
 * Validate a file name (including extension). Returns an error key or null if valid.
 *
 * Error keys are mapped to localized strings at the call site.
 */
export const validateFileName = (name: string): FileNameErrorKey | null => {
  if (!name.trim()) return "required";
  if (INVALID_CHARS.test(name)) return "invalid-chars";
  if (name.length > MAX_LENGTH) return "too-long";
  return null;
};

/**
 * Update only the extension of a file name when format changes.
 * If no extension exists, appends one.
 * Only replaces known export format extensions (csv, bai2).
 */
export const updateFileExtension = (
  fileName: string,
  newFormat: ExportFormat
): string => {
  const newExt = newFormat === "bai2" ? "bai2" : "csv";
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) return `${fileName}.${newExt}`;
  const oldExt = fileName.substring(lastDotIndex + 1).toLowerCase();
  if (oldExt === "csv" || oldExt === "bai2") {
    return `${fileName.substring(0, lastDotIndex)}.${newExt}`;
  }
  // Trailing dot (empty extension) — replace with new extension
  if (oldExt === "") {
    return `${fileName.substring(0, lastDotIndex)}.${newExt}`;
  }
  return `${fileName}.${newExt}`;
};
